<script setup lang="ts">
import { computed, h, onMounted, provide, ref } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { NButton, NConfigProvider, NDialogProvider, NDropdown, NIcon, NLayout, NLayoutContent, NLayoutHeader, NLayoutSider, NMenu, NMessageProvider, darkTheme, zhCN, dateZhCN, type MenuOption } from 'naive-ui'
import { AnalyticsOutline, BriefcaseOutline, CubeOutline, HomeOutline, SettingsOutline, StorefrontOutline, TrophyOutline, PersonCircleOutline } from '@vicons/ionicons5'
import { useAuthStore } from './stores/auth'
import { api } from './api'
import { darkThemeOverrides, lightThemeOverrides } from './theme'
import ChangePasswordModal from './components/ChangePasswordModal.vue'
import BearMark from './components/BearMark.vue'

const route = useRoute(), router = useRouter(), auth = useAuthStore()
const collapsed = ref(false), dark = ref(localStorage.getItem('xhs_tech_theme') === 'dark')
provide('workbench-dark', dark)
const serviceReady = ref(false)
const passwordOpen = ref(false)
const runtimeMode = ref('mock')
onMounted(async () => { try { const d: any = await api.get('/health'); runtimeMode.value = d.executor_mode; serviceReady.value=d.status==='ok' } catch {} })
const themeOverrides = computed(() => dark.value&&!route.meta.public ? darkThemeOverrides : lightThemeOverrides)
const icon = (component: any) => () => h(NIcon, null, { default: () => h(component) })
const menu = computed<MenuOption[]>(() => [
  { label: '今日工作台', key: '/dashboard', icon: icon(HomeOutline) },
  ...(['admin','shop_ops'].includes(auth.user?.role || '') ? [{ label: '店铺工作台', key: '/shops', icon: icon(StorefrontOutline) }] : []),
  ...(auth.user?.role === 'admin' ? [{ label: 'IP 管理', key: '/proxies', icon: icon(AnalyticsOutline) }] : []),
  { label: '商品工作台', key: '/products', icon: icon(CubeOutline) },
  ...(auth.user?.role === 'operator' ? [{ label: '小组人员', key: '/group-members', icon: icon(PersonCircleOutline) }] : []),
  { label: '作业中心', key: '/jobs', icon: icon(BriefcaseOutline) },
  { label: '经营分析', key: '/analytics', icon: icon(AnalyticsOutline) },
  { label: '业绩排行', key: '/ranking', icon: icon(TrophyOutline) },
  ...(auth.user?.role === 'admin' ? [{ type: 'divider' as const, key: 'system-divider' }, { label: '系统管理', key: '/system', icon: icon(SettingsOutline) }] : [])
].filter(item => auth.user?.role !== 'shop_ops' || ['/shops', '/products'].includes(String(item.key))).filter(item => auth.user?.role !== 'operator' || ['/dashboard', '/products', '/group-members', '/jobs'].includes(String(item.key))))
const selectedMenu = computed(() => {
  for (const prefix of ['/products', '/jobs', '/analytics', '/system']) if (route.path.startsWith(prefix)) return prefix
  return route.path
})
const title = computed(() => String(route.meta.title || '运营中心'))
function goMenu(key: string) {
  const landing: Record<string, string> = { '/products': '/products/catalog', '/jobs': '/jobs/all', '/analytics': '/analytics/overview', '/system': '/system/users' }
  router.push(landing[key] || key)
}
function accountAction(key: string) {
  if (key === 'password') passwordOpen.value = true
  if (key === 'theme') { dark.value = !dark.value; localStorage.setItem('xhs_tech_theme', dark.value ? 'dark' : 'light') }
  if (key === 'logout') { void auth.logout().finally(() => router.push('/login')) }
}
</script>

<template>
  <n-config-provider :theme="dark&&!route.meta.public ? darkTheme : null" :theme-overrides="themeOverrides" :locale="zhCN" :date-locale="dateZhCN">
    <n-dialog-provider><n-message-provider>
      <router-view v-if="route.meta.public" />
      <n-layout v-else has-sider class="app-shell" :class="{'tech-theme':dark,'light-theme':!dark}">
        <n-layout-sider bordered collapse-mode="width" :collapsed-width="72" :width="236" :collapsed="collapsed" show-trigger @collapse="collapsed=true" @expand="collapsed=false">
          <div class="brand" :class="{ compact: collapsed }"><span class="brand-mark bear-logo"><BearMark/></span><span v-if="!collapsed"><strong>小野熊</strong><small>统一运营工作台 · V3</small></span></div>
          <n-menu :value="selectedMenu" :options="menu" :collapsed="collapsed" :collapsed-width="72" :collapsed-icon-size="22" @update:value="goMenu(String($event))" />
          <div v-if="!collapsed" class="sidebar-brand-footer"><div class="sidebar-mascot"><BearMark mascot/><p>用简单的工具<br/><span>做好经营的每一天</span></p></div><div class="safe-mode" :class="{'live-mode':runtimeMode==='live'}"><span></span><div><strong>{{runtimeMode==='live'?'真实执行模式':'安全演练模式'}}</strong><small>{{runtimeMode==='live'?'操作会访问商家后台':'不会访问商家后台'}}</small></div></div></div>
        </n-layout-sider>
        <div class="workspace-main">
          <n-layout-header bordered class="topbar">
            <div><p class="eyebrow">XHS OPERATIONS · V3</p><h1>{{ title }}</h1></div>
            <div class="topbar-account"><span class="service-state" :class="{ready:serviceReady}"><i></i>{{serviceReady?'服务已连接':'服务未连接'}}</span><n-dropdown trigger="click" :options="[{label:'修改密码',key:'password'},{label: dark?'切换浅色':'切换深色',key:'theme'},{label:'退出登录',key:'logout'}]" @select="accountAction">
              <n-button quaternary class="account"><BearMark class="bear-logo account-avatar"/><span>{{ auth.user?.real_name || auth.user?.username }}</span></n-button>
            </n-dropdown></div>
          </n-layout-header>
          <main class="content" :key="route.path"><router-view /></main>
        </div>
      </n-layout>
      <ChangePasswordModal v-if="!route.meta.public" v-model:show="passwordOpen"/>
    </n-message-provider></n-dialog-provider>
  </n-config-provider>
</template>
